import React, { useMemo, memo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

let hasAnimatedInThisPageSession = false;

const TAB_DEF = {
  sicaklik: {
    title: "Sıcaklık",
    icKey: "SICAKLIK_IC",
    disKey: "SICAKLIK_DIS",
    unit: "°C",
    icLabel: "İç Sıcaklık",
    disLabel: "Dış Sıcaklık",
  },
  nem: {
    title: "Nem",
    icKey: "NEM_IC",
    disKey: "NEM_DIS",
    unit: "%",
    icLabel: "İç Nem",
    disLabel: "Dış Nem",
  },
  basinc: {
    title: "Basınç",
    icKey: "BASINC_IC",
    disKey: "BASINC_DIS",
    unit: "Pa",
    icLabel: "İç Basınç",
    disLabel: "Dış Basınç",
  },
};

const timeFromZaman = (z) => {
  if (!z) return "";
  const p = z.split(" ");
  if (p.length < 2) return "";
  return p[1].slice(0, 5);
};

const parseTrDateTime = (zamanStr) => {
  if (!zamanStr) return null;
  const [datePart, timePart] = zamanStr.split(" ");
  if (!datePart || !timePart) return null;

  const [d, m, y] = datePart.split(".");
  if (!d || !m || !y) return null;

  return new Date(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T${timePart}`);
};

function SensorCharts({
  data = [],
  tab = "sicaklik",
  onTabChange,
  loading = false,
  error = "",
  canEdit = true,
}) {
  const cfg = TAB_DEF[tab];
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [viewMode, setViewMode] = useState("grafik");

  const sortedRawData = useMemo(() => {
    return [...data].sort((a, b) => {
      const da = Number(a?.DAKIKA);
      const db = Number(b?.DAKIKA);
      if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
      return String(a?.ZAMAN_STR || "").localeCompare(String(b?.ZAMAN_STR || ""));
    });
  }, [data]);

  const chartData = useMemo(() => {
    return sortedRawData.map((d) => ({
      time: timeFromZaman(d.ZAMAN_STR),
      ic: d[cfg.icKey],
      dis: d[cfg.disKey],
    }));
  }, [sortedRawData, cfg]);

  const tableData = useMemo(() => {
    return [...sortedRawData]
      .reverse() // en yeni üstte
      .map((d) => ({
        zamanStr: d.ZAMAN_STR,
        saat: timeFromZaman(d.ZAMAN_STR),
        ic: d[cfg.icKey],
        dis: d[cfg.disKey],
      }));
  }, [sortedRawData, cfg]);

  const interval = Math.max(0, Math.ceil(chartData.length / 8) - 1);
  const hasData = Array.isArray(chartData) && chartData.length > 0;

  const latestRecord = useMemo(() => {
    if (!data?.length) return null;

    return [...data].sort((a, b) => {
      const ta = parseTrDateTime(a.ZAMAN_STR)?.getTime() || 0;
      const tb = parseTrDateTime(b.ZAMAN_STR)?.getTime() || 0;
      return tb - ta;
    })[0];
  }, [data]);

  const offlineInfo = useMemo(() => {
    if (!latestRecord?.ZAMAN_STR) {
      return { isOffline: false, diffMin: 0, lastSeen: "" };
    }

    const lastDate = parseTrDateTime(latestRecord.ZAMAN_STR);
    if (!lastDate) {
      return { isOffline: false, diffMin: 0, lastSeen: "" };
    }

    const diffMin = Math.floor((Date.now() - lastDate.getTime()) / 60000);
    return {
      isOffline: diffMin > 11,
      diffMin,
      lastSeen: latestRecord.ZAMAN_STR,
    };
  }, [latestRecord]);

  useEffect(() => {
    if (!loading && hasData && !hasAnimatedInThisPageSession) {
      hasAnimatedInThisPageSession = true;
      setShouldAnimate(true);

      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 1400);

      return () => clearTimeout(timer);
    }
  }, [loading, hasData]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        bgcolor: "#fff",
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 6px 20px rgba(17,24,39,0.04)",
      }}
    >
      <Stack spacing={2}>
        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
          Sensör Verileri
        </Typography>

        {canEdit === false && <Chip size="small" label="Sadece görüntüleme" />}

        {offlineInfo.isOffline && (
          <Box
            sx={{
              px: 2,
              py: 1.2,
              borderRadius: 2.5,
              bgcolor: "rgba(245, 158, 11, 0.10)",
              border: "1px solid rgba(245, 158, 11, 0.28)",
              color: "#92400E",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            Bir süredir veri gelmiyor • Son veri: {offlineInfo.lastSeen} • {offlineInfo.diffMin} dk
          </Box>
        )}

        {/* GRAFİK / TABLO */}
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={viewMode}
          onChange={(_, v) => v && setViewMode(v)}
          sx={{
            width: "100%",
            bgcolor: "#F3F4F6",
            p: 0.5,
            borderRadius: 999,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.5,
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: 999,
              py: 1,
              fontSize: { xs: 15, md: 18 },
              fontWeight: 900,
              color: "#111827",
            },
            "& .Mui-selected": {
              bgcolor: "#fff !important",
              color: "#111827",
              boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
            },
          }}
        >
          <ToggleButton value="grafik">GRAFİK</ToggleButton>
          <ToggleButton value="tablo">TABLO</ToggleButton>
        </ToggleButtonGroup>

        {/* SICAKLIK / NEM / BASINÇ */}
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={tab}
          onChange={(_, v) => v && onTabChange(v)}
          sx={{
            width: "100%",
            bgcolor: "#F3F4F6",
            p: 0.4,
            borderRadius: 999,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 0.4,
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: 999,
              py: 0.55,
              fontSize: 12,
              fontWeight: 800,
              color: "#6B7280",
            },
            "& .Mui-selected": {
              bgcolor: "#fff !important",
              color: "#111827",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            },
          }}
        >
          <ToggleButton value="sicaklik">SICAKLIK</ToggleButton>
          <ToggleButton value="nem">NEM</ToggleButton>
          <ToggleButton value="basinc">BASINÇ</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Typography sx={{ color: "#6B7280" }}>Yükleniyor...</Typography>
        ) : error ? (
          <Typography sx={{ color: "#DC2626" }}>{error}</Typography>
        ) : !hasData ? (
          <Box
            sx={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed rgba(0,0,0,0.15)",
              borderRadius: 2,
              bgcolor: "#F9FAFB",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#6B7280" }}>
              Bugüne ait veri yok
            </Typography>
          </Box>
        ) : viewMode === "grafik" ? (
          <Box sx={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  interval={interval}
                  tick={{ fontSize: 11 }}
                  tickMargin={8}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${v}${cfg.unit}`}
                />
                <Tooltip
                  formatter={(v, name) => [
                    `${Number(v).toFixed(2)}${cfg.unit}`,
                    name === "ic" ? "İç" : "Dış",
                  ]}
                  labelFormatter={(l) => `Saat: ${l}`}
                />
                <Line
                  type="monotone"
                  dataKey="ic"
                  name="ic"
                  stroke="#EF4444"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={shouldAnimate}
                  animationDuration={1200}
                  animationEasing="ease-out"
                />
                <Line
                  type="monotone"
                  dataKey="dis"
                  name="dis"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={shouldAnimate}
                  animationDuration={1200}
                  animationEasing="ease-out"
                  animationBegin={150}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <TableContainer
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 2,
              maxHeight: 360,
              bgcolor: "#fff",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Saat</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>{cfg.icLabel}</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>{cfg.disLabel}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData.map((row, i) => (
                  <TableRow
                    key={`${row.zamanStr}-${i}`}
                    sx={{
                      bgcolor: i === 0 ? "#FFF7ED" : "inherit",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>{row.saat}</TableCell>
                    <TableCell>
                      {typeof row.ic === "number" ? row.ic.toFixed(2) : row.ic} {cfg.unit}
                    </TableCell>
                    <TableCell>
                      {typeof row.dis === "number" ? row.dis.toFixed(2) : row.dis} {cfg.unit}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
}

export default memo(SensorCharts);